/**
 * Account Switching Service
 * Manages multiple user account sessions for seamless account switching
 */

const STORAGE_KEYS = {
  ACCOUNTS: 'zoobbe_accounts',
  ACTIVE_ACCOUNT: 'zoobbe_active_account',
  ACCOUNT_SESSIONS: 'zoobbe_account_sessions'
};

class AccountSwitchingService {
  constructor() {
    this.accounts = this.loadAccounts();
    this.activeAccountId = this.loadActiveAccountId();
  }

  /**
   * Load all stored accounts from localStorage
   */
  loadAccounts() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ACCOUNTS);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading accounts:', error);
      return [];
    }
  }

  /**
   * Save accounts to localStorage
   */
  saveAccounts(accounts) {
    try {
      localStorage.setItem(STORAGE_KEYS.ACCOUNTS, JSON.stringify(accounts));
      this.accounts = accounts;
    } catch (error) {
      console.error('Error saving accounts:', error);
    }
  }

  /**
   * Load active account ID
   */
  loadActiveAccountId() {
    return localStorage.getItem(STORAGE_KEYS.ACTIVE_ACCOUNT);
  }

  /**
   * Set active account ID
   */
  setActiveAccountId(accountId) {
    if (accountId) {
      localStorage.setItem(STORAGE_KEYS.ACTIVE_ACCOUNT, accountId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.ACTIVE_ACCOUNT);
    }
    this.activeAccountId = accountId;
  }

  /**
   * Add or update an account
   */
  addAccount(userData) {
    const account = {
      id: userData._id,
      username: userData.username,
      email: userData.email,
      name: userData.name,
      profilePicture: userData.profilePicture,
      verified: userData.verified,
      lastActive: new Date().toISOString(),
      addedAt: new Date().toISOString()
    };

    const existingIndex = this.accounts.findIndex(acc => acc.id === account.id);
    
    if (existingIndex >= 0) {
      // Update existing account
      this.accounts[existingIndex] = { ...this.accounts[existingIndex], ...account };
    } else {
      // Add new account
      this.accounts.push(account);
    }

    this.saveAccounts(this.accounts);
    return account;
  }

  /**
   * Remove an account
   */
  removeAccount(accountId) {
    this.accounts = this.accounts.filter(acc => acc.id !== accountId);
    this.saveAccounts(this.accounts);

    // If removing active account, clear active account
    if (this.activeAccountId === accountId) {
      this.setActiveAccountId(null);
    }

    // Remove session data
    this.removeAccountSession(accountId);
  }

  /**
   * Get all accounts
   */
  getAccounts() {
    return this.accounts;
  }

  /**
   * Get active account
   */
  getActiveAccount() {
    if (!this.activeAccountId) return null;
    return this.accounts.find(acc => acc.id === this.activeAccountId);
  }

  /**
   * Update last active timestamp for an account
   */
  updateLastActive(accountId) {
    const accountIndex = this.accounts.findIndex(acc => acc.id === accountId);
    if (accountIndex >= 0) {
      this.accounts[accountIndex].lastActive = new Date().toISOString();
      this.saveAccounts(this.accounts);
    }
  }

  /**
   * Store session data for an account
   */
  storeAccountSession(accountId, sessionData) {
    try {
      const sessions = this.loadAccountSessions();
      sessions[accountId] = {
        ...sessionData,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(STORAGE_KEYS.ACCOUNT_SESSIONS, JSON.stringify(sessions));
    } catch (error) {
      console.error('Error storing account session:', error);
    }
  }

  /**
   * Load session data for an account
   */
  loadAccountSession(accountId) {
    try {
      const sessions = this.loadAccountSessions();
      return sessions[accountId] || null;
    } catch (error) {
      console.error('Error loading account session:', error);
      return null;
    }
  }

  /**
   * Load all account sessions
   */
  loadAccountSessions() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ACCOUNT_SESSIONS);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error loading account sessions:', error);
      return {};
    }
  }

  /**
   * Remove session data for an account
   */
  removeAccountSession(accountId) {
    try {
      const sessions = this.loadAccountSessions();
      delete sessions[accountId];
      localStorage.setItem(STORAGE_KEYS.ACCOUNT_SESSIONS, JSON.stringify(sessions));
    } catch (error) {
      console.error('Error removing account session:', error);
    }
  }

  /**
   * Clear all account data (for logout all)
   */
  clearAllAccounts() {
    localStorage.removeItem(STORAGE_KEYS.ACCOUNTS);
    localStorage.removeItem(STORAGE_KEYS.ACTIVE_ACCOUNT);
    localStorage.removeItem(STORAGE_KEYS.ACCOUNT_SESSIONS);
    this.accounts = [];
    this.activeAccountId = null;
  }

  /**
   * Check if user has multiple accounts
   */
  hasMultipleAccounts() {
    return this.accounts.length > 1;
  }

  /**
   * Get account count
   */
  getAccountCount() {
    return this.accounts.length;
  }
}

// Create singleton instance
const accountSwitchingService = new AccountSwitchingService();

export default accountSwitchingService;
